import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Loader2, Check } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      await axios.post(`${API}/leads/contact`, {
        ...formData,
        source: 'contact_page'
      });
      setStatus('success');
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Something went wrong');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-taxera-charcoal mb-4">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
            <div className="h-1 w-20 taxera-gradient mx-auto mt-6 rounded-full" />
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h3 className="font-heading text-xl font-semibold text-taxera-charcoal mb-4">
                  {t('contact.infoTitle')}
                </h3>
                <p className="text-slate-600 mb-6">
                  {t('contact.infoSubtitle')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-taxera-blue/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-taxera-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium text-taxera-charcoal">{t('contact.address')}</h4>
                    <p className="text-slate-600 text-sm">
                      Sihleggstrasse 23<br />
                      8832 Wollerau, Switzerland
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-taxera-teal/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-taxera-teal" />
                  </div>
                  <div>
                    <h4 className="font-medium text-taxera-charcoal">{t('contact.email')}</h4>
                    <a href="mailto:office@taxera.tech" className="text-taxera-blue hover:underline text-sm">
                      office@taxera.tech
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-taxera-mint/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-taxera-charcoal">{t('contact.hours')}</h4>
                    <p className="text-slate-600 text-sm">
                      {t('contact.hoursValue')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2 bg-slate-50 rounded-3xl p-8"
            >
              {status === 'success' ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">
                      {t('contact.form.success')}
                    </h3>
                    <p className="text-slate-600">
                      {t('contact.form.successDetail')}
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.fullName')} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 focus:border-taxera-blue transition-all"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.workEmail')} *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 focus:border-taxera-blue transition-all"
                        placeholder="john@company.com"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.company')}
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 focus:border-taxera-blue transition-all"
                        placeholder="Company Inc."
                        data-testid="contact-company-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('contact.form.phone')}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 focus:border-taxera-blue transition-all"
                        placeholder="+41 XX XXX XX XX"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 focus:border-taxera-blue transition-all resize-none"
                      placeholder={t('contact.form.messagePlaceholder')}
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 taxera-gradient text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="contact-submit-btn"
                  >
                    {status === 'loading' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {t('contact.form.send')}
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
